import {
  FileImageVariant,
  FileType,
  useCreateFileMutation,
  useUpdateUserAvatar,
} from '#apollo/schema';
import {Avatar, Text} from '#component/base';
import {editProfileQuery} from '#component/page/EditProfile/useEditProfile';
import {useToastQueue} from '#hook';
import {uploadFile} from '#service/file/upload';
import {sprinkles} from '#style/sprinkles';

import {AvatarImageInputProps} from './AvatarImageInputAccount.types';

import * as css from './AvatarImageInputAccount.css';

const VARIANT = FileImageVariant.Size_160x160;

export const AvatarImageInputAccount = ({
  image,
  text,
  setLoading,
  loading,
}: AvatarImageInputProps) => {
  const [createFile] = useCreateFileMutation();
  const [updateUserAvatar] = useUpdateUserAvatar({
    refetchQueries: [
      {query: editProfileQuery, variables: {imageVariant: FileImageVariant.Size_160x160}},
    ],
  });
  const {addToast} = useToastQueue();
  const handleChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLoading(true);

      try {
        const {data} = await createFile({
          variables: {input: {contentType: file.type, name: file.name, type: FileType.Image}},
        });
        const {id, uploadUrl} = data?.createFile || {};

        if (id && uploadUrl) {
          // TODO: Currently the backend only supports uploads for jpeg file formats
          const uploadSuccess = await uploadFile({file, url: uploadUrl});

          if (uploadSuccess) {
            await updateUserAvatar({variables: {input: {avatarFileId: id}, imageVariant: VARIANT}});
          }
        }

        setLoading(false);
      } catch (e) {
        addToast({children: 'Unable to change avatar! Please contact support.'});
        setLoading(false);
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await updateUserAvatar({variables: {input: {avatarFileId: null}, imageVariant: VARIANT}});
      addToast({children: 'Avatar updated'});
    } catch (e) {
      console.warn('Error', e);
      addToast({children: 'Unable to update avatar! Please contact support.'});
    }
  };

  return (
    <div
      className={sprinkles({
        flex: 'inline',
        direction: 'row',
        gap: [16, 32],
        width: 'full',
      })}>
      <div
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          placeContent: ['center', 'start'],
          placeItems: ['center', 'start'],
          position: 'relative',
        })}>
        <Avatar
          image={image}
          text={text}
          className={css.avatarCustom}
          size="medium"
          textSize="small"
          color="grapefruit"
        />

        <input
          aria-label="Change profile photo"
          className={css.input}
          disabled={loading}
          onChange={handleChangeAvatar}
          type="file"
          accept="image/*"
        />
      </div>

      <div
        className={sprinkles({
          flex: 'flex',
          direction: ['col', 'row'],
          gap: 12,
          width: 'full',
          placeItems: 'center',
        })}>
        <Text as="button" className={css.btnInput}>
          <input
            className={css.input}
            disabled={loading}
            onChange={handleChangeAvatar}
            type="file"
            accept="image/*"
          />
          <Text color="eggplantDark" className={css.innerBtnText}>
            Upload Photo
          </Text>
        </Text>
        {!!image && (
          <Text as="button" className={css.btnInput} onClick={handleRemoveAvatar}>
            <Text color="eggplantDark" className={css.innerBtnText}>
              Delete Photo
            </Text>
          </Text>
        )}
      </div>
    </div>
  );
};
